import React, { FC } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { FaFacebook, FaYelp } from 'react-icons/fa';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { ToastContainer, toast } from 'react-toastify';
import cn from 'classnames';
import * as Yup from 'yup';
import 'yup-phone-lite';
import { usePlausible } from 'next-plausible';
import { COMPANY_EMAIL, COMPANY_PHONE_NUMBER } from '@lib/constants';

import 'react-toastify/dist/ReactToastify.css';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactForm: FC = () => {
  const plausible = usePlausible();

  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  };

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, 'Too short!')
      .max(35, 'Too long!')
      .required('Required!'),
    lastName: Yup.string()
      .min(2, 'Too short!')
      .max(35, 'Too long!')
      .required('Required!'),
    email: Yup.string().email().required('Required!'),
    phone: Yup.string()
      .phone('US', 'Please enter a valid phone number')
      .required('A phone number is required'),
    subject: Yup.string()
      .min(2, 'Too short!')
      .max(35, 'Too long!')
      .required('Required!'),
    message: Yup.string().min(2).max(500).required('Required!'),
  });
  return (
    <section className="relative bg-white" aria-labelledby="contact-heading">
      <div className="absolute h-1/2 w-full bg-stone-50" aria-hidden="true" />
      {/* Decorative dot pattern */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <svg
          className="absolute top-0 right-0 -translate-y-16 translate-x-1/2 transform sm:translate-x-1/4 md:-translate-y-24 lg:-translate-y-72"
          width={404}
          height={384}
          fill="none"
          viewBox="0 0 404 384"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect
                x={0}
                y={0}
                width={4}
                height={4}
                className="text-stone-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            width={404}
            height={384}
            fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)"
          />
        </svg>
      </div>
      <div className="mx-auto mb-10 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative bg-white shadow-xl">
          <h2 id="contact-heading" className="sr-only">
            Contact us
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3">
            {/* Contact information */}
            <div className="relative overflow-hidden bg-gradient-to-b from-red-600 to-red-700 py-10 px-6 sm:px-10 xl:p-12">
              {/* Decorative angle backgrounds */}
              <div
                className="pointer-events-none absolute inset-0 sm:hidden"
                aria-hidden="true"
              >
                <svg
                  className="absolute inset-0 h-full w-full"
                  width={343}
                  height={388}
                  viewBox="0 0 343 388"
                  fill="none"
                  preserveAspectRatio="xMidYMid slice"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M-99 461.107L608.107-246l707.103 707.107-707.103 707.103L-99 461.107z"
                    fill="url(#linear1)"
                    fillOpacity=".1"
                  />
                  <defs>
                    <linearGradient
                      id="linear1"
                      x1="254.553"
                      y1="107.554"
                      x2="961.66"
                      y2="814.66"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#fff" />
                      <stop offset={1} stopColor="#fff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div
                className="pointer-events-none absolute top-0 right-0 bottom-0 hidden w-1/2 sm:block lg:hidden"
                aria-hidden="true"
              >
                <svg
                  className="absolute inset-0 h-full w-full"
                  width={359}
                  height={339}
                  viewBox="0 0 359 339"
                  fill="none"
                  preserveAspectRatio="xMidYMid slice"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M-161 382.107L546.107-325l707.103 707.107-707.103 707.103L-161 382.107z"
                    fill="url(#linear2)"
                    fillOpacity=".1"
                  />
                  <defs>
                    <linearGradient
                      id="linear2"
                      x1="192.553"
                      y1="28.553"
                      x2="899.66"
                      y2="735.66"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#fff" />
                      <stop offset={1} stopColor="#fff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div
                className="pointer-events-none absolute top-0 right-0 bottom-0 hidden w-1/2 lg:block"
                aria-hidden="true"
              >
                <svg
                  className="absolute inset-0 h-full w-full"
                  width={160}
                  height={678}
                  viewBox="0 0 160 678"
                  fill="none"
                  preserveAspectRatio="xMidYMid slice"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M-161 679.107L546.107-28l707.103 707.107-707.103 707.103L-161 679.107z"
                    fill="url(#linear3)"
                    fillOpacity=".1"
                  />
                  <defs>
                    <linearGradient
                      id="linear3"
                      x1="192.553"
                      y1="325.553"
                      x2="899.66"
                      y2="1032.66"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#fff" />
                      <stop offset={1} stopColor="#fff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-100">
                Contact information
              </h3>
              <p className="mt-6 max-w-3xl text-base text-gray-100">
                Let&apos;s get this conversation started. Tell us a little bit
                about yourself and we&apos;ll get in touch as soon as we can.
              </p>
              <dl className="mt-8 space-y-6">
                <dt>
                  <span className="sr-only">Phone number</span>
                </dt>
                <dd className="flex text-base text-white">
                  <PhoneIcon
                    className="h-6 w-6 shrink-0 text-gray-200"
                    aria-hidden="true"
                  />
                  <span className="ml-3">
                    <a
                      href={`tel:${COMPANY_PHONE_NUMBER}`}
                      onClick={() => plausible('callPhoneNumber')}
                    >
                      {COMPANY_PHONE_NUMBER}
                    </a>
                  </span>
                </dd>
                <dt>
                  <span className="sr-only">Email</span>
                </dt>
                <dd className="flex text-base text-white">
                  <EnvelopeIcon
                    className="h-6 w-6 shrink-0 text-gray-200"
                    aria-hidden="true"
                  />
                  <span className="ml-3">{COMPANY_EMAIL}</span>
                </dd>
              </dl>
              <ul role="list" className="mt-8 flex space-x-12">
                <li>
                  <a
                    className="text-gray-200 hover:text-gray-100"
                    href="https://www.facebook.com/HLBElectric/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Facebook</span>
                    <FaFacebook className="h-7 w-7" />
                  </a>
                </li>
                <li>
                  <a
                    className="text-gray-200 hover:text-gray-100"
                    href="https://www.yelp.com/biz/hlb-electric-maricopa"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="sr-only">Yelp</span>
                    <FaYelp className="h-7 w-7" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact form */}
            <div className="py-10 px-6 sm:px-10 lg:col-span-2 xl:p-12">
              <h3 className="text-lg font-medium text-stone-900">
                Send us a message
              </h3>
              <Formik
                initialValues={initialValues}
                onSubmit={async (
                  values: FormValues,
                  { setSubmitting, resetForm }: FormikHelpers<FormValues>
                ) => {
                  setSubmitting(true);
                  await toast.promise(
                    fetch('/api/contact', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify(values),
                    }).then(() => resetForm()),
                    {
                      pending: {
                        render() {
                          return 'Submitting your request';
                        },
                        icon: false,
                        toastId: 'contact-form-pending',
                      },
                      success: {
                        render() {
                          return 'Thank you for reaching out to us! We will be in contact shortly!';
                        },
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: true,
                        pauseOnHover: true,
                        pauseOnFocusLoss: false,
                        draggable: true,
                        toastId: 'contact-form-success',
                      },
                      error: {
                        render() {
                          return 'There was an issue submitting your request. Please try again later.';
                        },
                        toastId: 'contact-form-error',
                      },
                    }
                  );
                  plausible('submitContactForm', {
                    props: {
                      page: 'Contact',
                    },
                  });
                  setSubmitting(false);
                }}
                validationSchema={validationSchema}
              >
                {({ touched, errors, isSubmitting, isValid, dirty }) => (
                  <Form className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-stone-900"
                      >
                        First name
                      </label>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.firstName
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          type="text"
                          name="firstName"
                          id="firstName"
                          autoComplete="given-name"
                          aria-invalid={errors.firstName}
                        />
                        {errors.firstName && touched.firstName ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="firstName">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="firstName-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-stone-900"
                      >
                        Last name
                      </label>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.lastName
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          type="text"
                          name="lastName"
                          id="lastName"
                          autoComplete="family-name"
                          aria-invalid={errors.lastName}
                        />
                        {errors.lastName && touched.lastName ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="lastName">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="lastName-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-stone-900"
                      >
                        Email
                      </label>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.email
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          aria-invalid={errors.email}
                        />
                        {errors.email && touched.email ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="email">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="email-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div>
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-stone-900"
                        >
                          Phone
                        </label>
                      </div>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.phone
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          type="text"
                          name="phone"
                          id="phone"
                          autoComplete="tel"
                          aria-invalid={errors.phone}
                        />
                        {errors.phone && touched.phone ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="phone">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="phone-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-stone-900"
                      >
                        Subject
                      </label>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.subject
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          type="text"
                          name="subject"
                          id="subject"
                          aria-invalid={errors.subject}
                        />
                        {errors.subject && touched.subject ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="subject">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="subject-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between">
                        <label
                          htmlFor="message"
                          className="block text-sm font-medium text-stone-900"
                        >
                          Message
                        </label>
                        <span
                          id="message-max"
                          className="text-sm text-stone-500"
                        >
                          Max. 500 characters
                        </span>
                      </div>
                      <div className="relative mt-1">
                        <Field
                          className={cn(
                            errors.message
                              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500'
                              : 'border-stone-300 text-stone-900 focus:border-emerald-500 focus:ring-emerald-500',
                            'block w-full rounded-md py-3 pr-10 sm:text-sm'
                          )}
                          as="textarea"
                          id="message"
                          name="message"
                          rows={4}
                          aria-invalid={errors.message}
                          aria-describedby="message-max"
                        />
                        {errors.message && touched.message ? (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ExclamationCircleIcon
                              className="h-5 w-5 text-red-500"
                              aria-hidden="true"
                            />
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="message">
                        {(msg) => (
                          <p
                            className="mt-2 text-sm text-red-600"
                            id="message-error"
                          >
                            {msg}
                          </p>
                        )}
                      </ErrorMessage>
                    </div>
                    <div className="sm:col-span-2 sm:flex sm:justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting || !(isValid && dirty)}
                        className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-red-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                      >
                        Submit
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
              <ToastContainer />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
